// The one player bar: the active line's play / pause, loop, slow (0.75×), its sources and close.
// Phone (and any PC page without a spread): a 56px bar between the page and the tabs, only while a
// line is active — the page above it shrinks by its height, so nothing is covered. PC with a spread:
// at the foot of lane B (Spread.tsx renders it there). Nothing animates.
import { Hv } from '../components/Hv';
import { Icon } from '../components/Icon';
import { useT } from '../i18n';
import { useMargin } from './context';
import { playLine, setLoop, setSlow, stopPlayer, togglePlayer, usePlayer } from './player';

export function PlayerBar({ where }: { where: 'shell' | 'lane' }) {
  const t = useT();
  const P = t.margin.player;
  const { lineInfo, actions } = useMargin();
  const player = usePlayer();

  // (the sound stops when its line is closed — Back, another page: MarginProvider, which outlives
  // the lane's bar)

  if (!lineInfo || !lineInfo.sources.length) return null;
  const mine = player.lineId === lineInfo.id;
  const playing = mine && player.playing;
  const at = mine ? player.source : 0;
  const src = lineInfo.sources[at] ?? lineInfo.sources[0]!;
  const many = lineInfo.sources.length > 1;
  return (
    <section className={`player is-${where}`} aria-label={P.label}>
      {/* the label says what a press does (Kuuntele / Tauko); no pressed state on top of it */}
      <button type="button" className="player-play" onClick={() => togglePlayer(lineInfo)}>
        <Icon name={playing ? 'pause' : 'play'} size={20} />
        <span className="vh">{playing ? P.pause : P.play}</span>
      </button>
      <div className="player-what">
        {/* a part of a sequence shows its own line; a line that names itself ("Daenerys · GoT 3×04": a
            show line's whole text would not fit) its title */}
        {src.hv ? (
          <Hv className="player-hv">{src.hv}</Hv>
        ) : lineInfo.title ? (
          <span className="player-hv player-title">{lineInfo.title}</span>
        ) : (
          <Hv className="player-hv">{lineInfo.hv}</Hv>
        )}
        <span className="player-src">{src.label}</span>
        {/* mounted empty, filled when a recording fails: the change is what gets announced */}
        <span className="player-err" role="status">
          {mine && player.failed ? P.failed : null}
        </span>
      </div>
      {many ? (
        // the next recording of the line (Peterson's second take, the other one of you)
        <button type="button" className="player-opt" onClick={() => playLine(lineInfo, (at + 1) % lineInfo.sources.length)}>
          <span className="tnum">{P.source(at + 1, lineInfo.sources.length)}</span>
        </button>
      ) : null}
      <button type="button" className="player-opt" aria-pressed={player.loop} onClick={() => setLoop(!player.loop)}>
        <Icon name="loop" size={18} />
        <span>{P.loop}</span>
      </button>
      <button type="button" className="player-opt" aria-pressed={player.slow} onClick={() => setSlow(!player.slow)}>
        <span>{P.slow}</span>
      </button>
      <button
        type="button"
        className="icon-btn player-close"
        aria-label={P.close}
        onClick={() => {
          stopPlayer();
          actions?.close('line');
        }}
      >
        <Icon name="close" />
      </button>
    </section>
  );
}
